import ComponentManager, {
	ComponentOptions,
	ComponentOptionsInterface, ComponentOptionsValuePlaceholder
} from "../../../componentFramework";
import EventListenerManager from "../../EventListenerManager";
import clamp from "../../../scripts/clamp";
import EventManager from "../../EventManager";
import ExpandableManager, {ExpandableManagerEventData} from "../ExpandableManager";

export interface ExpandableEventMap {
	"collapseStart": {expandable: ExpandableArea};
	"collapseEnd": {expandable: ExpandableArea};
	"expandStart": {expandable: ExpandableArea};
	"expandEnd": {expandable: ExpandableArea};
	"expandChange": {expandable: ExpandableArea, expanded: boolean};
}

const defaultOptions = {
	styleClass: {
		isReady: "expandableArea--isReady",
		isExpanded: "expandableArea--isExpanded"
	},

	transition: {
		speed: 1.5,
		minDuration: 200,
		maxDuration: 400
	},
	memorizeStateKey: ComponentOptionsValuePlaceholder.string.optional
};
export type DefaultOptionsType = typeof defaultOptions;

/**
 * Use an instance of ExpandableControl to control the expanding and collapsing of an ExpandableArea instance.
 */
@ComponentManager.register()
export default class ExpandableArea<InstanceOptions extends ComponentOptionsInterface = ComponentOptionsInterface> {
	public static componentStyleClass = "expandableArea";

	protected readonly options: ComponentOptions<DefaultOptionsType & InstanceOptions>;

	private readonly memorizeStateKey: string;
	protected static readonly memorizedExpandStates = new Map<string,boolean>();
	private readonly eventListenerManager = new EventListenerManager();

	private readonly eventManager = new EventManager<ExpandableEventMap>();
	public readonly addEventListener = this.eventManager.addEventListener;
	public readonly removeEventListener = this.eventManager.removeEventListener;

	constructor(protected readonly rootElement: HTMLElement, instanceOptions?: InstanceOptions) {
		this.options = new ComponentOptions(this, rootElement, defaultOptions).extend(instanceOptions);

		if (rootElement.id.length === 0) {
			throw new Error(`Element is missing "id" attribute which is required by this component.`);
		}

		this.memorizeStateKey = this.options.getValues().memorizeStateKey ?? rootElement.id;

		const managedExpandableManager = this.eventListenerManager.manage(ExpandableManager.getInstance());
		managedExpandableManager.addEventListener("expand", this.onManagerEvent);
		managedExpandableManager.addEventListener("collapse", this.onManagerEvent);
	}

	@ComponentManager.on("removed")
	protected componentRemoved() {
		this.eventManager.destroy();
		this.eventListenerManager.removeListeners();
	}

	@ComponentManager.on("constructed")
	protected componentConstructed() {
		const initExpandState = this.getMemorizedExpandState() ?? this.isExpanded();
		if (initExpandState) {
			ExpandableManager.getInstance().do("expand", [this.rootElement.id]);
		}
	}

	@ComponentManager.on("ready")
	protected componentReady() {
		// Monitor transitionrun and transitionend, and trigger events
		const rootElementPropertiesTransitioning = new Set<string>();
		const rootElementManaged = this.eventListenerManager.manage(this.rootElement);
		rootElementManaged.addEventListener("transitionrun", (ev: TransitionEvent) => {
			if (ev.target === ev.currentTarget) {
				if (rootElementPropertiesTransitioning.size === 0) {
					const eventData = {expandable: this};
					if (this.isExpanded()) {
						this.eventManager.notifyEventListeners("expandStart", eventData);
					} else {
						this.eventManager.notifyEventListeners("collapseStart", eventData);
					}
				}
				rootElementPropertiesTransitioning.add(ev.propertyName);
			}
		});
		rootElementManaged.addEventListener("transitionend", (ev: TransitionEvent) => {
			if (ev.target === ev.currentTarget) {
				rootElementPropertiesTransitioning.delete(ev.propertyName);
				if (rootElementPropertiesTransitioning.size === 0) {
					const eventData = {expandable: this};
					if (this.isExpanded()) {
						this.eventManager.notifyEventListeners("expandEnd", eventData);
					} else {
						this.eventManager.notifyEventListeners("collapseEnd", eventData);
					}
				}
			}
		});

		// Listen for "expandEnd" events
		this.eventListenerManager.manage(this).addEventListener("expandEnd", this.onExpandEnd);

		// Mark as ready which will let it start to animate using CSS transition
		requestAnimationFrame(() => {
			this.rootElement.classList.add(this.options.getValues().styleClass.isReady);
		});
	}

	private onManagerEvent = (data: ExpandableManagerEventData) => {
		let isExpand: boolean;
		if (data.type === "expand") {
			isExpand = true;
		} else if (data.type === "collapse") {
			isExpand = false;
		} else {
			return; // Nothing to do.
		}
		if (data.area !== undefined && data.area.indexOf(this.rootElement.id) !== -1) {
			this.setExpanded(isExpand);
		}
	}

	private isReady(): boolean {
		return this.rootElement.classList.contains(this.options.getValues().styleClass.isReady);
	}

	public isExpanded(): boolean {
		return this.rootElement.classList.contains(this.options.getValues().styleClass.isExpanded);
	}

	/* NOTE: Do NOT make this public. Changing the expand state should be done through ExpandableControl.
			If you do want to make a method, rename the below to be for internal use and create a new one
			that delegates to the proper place. */
	private setExpanded(isExpanding: boolean) {
		if (isExpanding === this.isExpanded()) {
			return; // No change so nothing to do.
		}

		const isImmediate = !this.isReady();

		const options = this.options.getValues();
		this.setMemorizedExpandState(isExpanding);

		if (isImmediate) {
			// Is immediate
			this.doExpandUpdate(true);
		} else {
			// Is not immediate
			if (isExpanding) {
				if (!this.isExpanded()) {
					this.rootElement.style.height = "0px";
				}
			} else {
				this.rootElement.style.height = this.rootElement.getBoundingClientRect().height + "px"; // Force the height property to be explicitly set so that CSS transitions work
			}
			requestAnimationFrame(() => {
				this.doExpandUpdate();
			});
		}

		this.rootElement.classList.toggle(options.styleClass.isExpanded, isExpanding);
		this.eventManager.notifyEventListeners("expandChange", {expandable: this, expanded: true});
	}

	private doExpandUpdate(isImmediate = false) {
		const isExpanded = this.isExpanded();

		if (isImmediate) {
			const eventData = {expandable: this};
			if (isExpanded) {
				this.eventManager.notifyEventListeners("expandEnd", eventData);
			} else {
				this.eventManager.notifyEventListeners("collapseEnd", eventData);
			}
		} else {
			// Update height
			const currentHeight = this.rootElement.getBoundingClientRect().height;
			const newHeight = isExpanded ? this.rootElement.scrollHeight : 0;

			const options = this.options.getValues();

			if (newHeight !== currentHeight) {
				// Set speed of transition (if support by browser, otherwise it will fallback to what is in the CSS)
				if (options.transition.speed !== null && this.rootElement.style.transitionDuration !== undefined) {
					let duration = 0;
					let heightDelta = Math.abs(newHeight - currentHeight);
					if (heightDelta !== 0) {
						duration = clamp(
							options.transition.minDuration,
							heightDelta * options.transition.speed,
							options.transition.maxDuration
						);
					}

					this.rootElement.style.setProperty("--transitionDuration", duration + "ms");
				}

				// Set new height
				this.rootElement.style.height = newHeight + "px";
			}
		}
	}

	private onExpandEnd = () => {
		this.rootElement.style.removeProperty("height");
	}

	private setMemorizedExpandState(isExpanded: boolean) {
		ExpandableArea.memorizedExpandStates.set(this.memorizeStateKey, isExpanded);
	}
	private getMemorizedExpandState(): boolean | undefined {
		return ExpandableArea.memorizedExpandStates.get(this.memorizeStateKey);
	}
}



/*

=========== LEAVE THIS HERE. These are notes on how we can do extending in a way that can be done over and over. =============

NOTES HERE ON HOW TO EXTEND:

import ComponentManager, {ComponentOptionsInterface} from "../../componentFramework";
import Expandable from "../Expandable";

const defaultOptions = {
	styleClass: {
		isReady: "inlineMenu--isReady",
		isExpanded: "inlineMenu--isExpanded",
		controlIsReady: "inlineMenu__control--isReady"
	}
};
export type DefaultOptionsType = typeof defaultOptions;

@ComponentManager.register()
export default class InlineMenu<InstanceOptions extends ComponentOptionsInterface = ComponentOptionsInterface>
		extends Expandable<DefaultOptionsType>  {
	public static componentStyleClass = "inlineMenu";

	constructor(rootElement: HTMLElement) {
		super(rootElement, defaultOptions);
	}
}

 */
